import type { AttachmentPickers } from 'owa-mail-ribbon-utils';
import type AttachmentMultiPicker from 'owa-attachment-filepicker/lib/types/AttachmentMultiPicker';
import type { CommandRibbonProps } from 'owa-command-ribbon';
import { GovernPriority } from 'owa-tti';
import type { GovernedTask } from 'owa-tti/lib/types/GovernedTask';
import { owaComputedFn } from 'owa-computed-fn';
import { getExtraTopRibbonControlsAfterTabs } from './util/getExtraTopRibbonControlsAfterTabs';
import { getExtraTopRibbonControlsBeforeTabs } from './util/getExtraTopRibbonControlsBeforeTabs';
import { getMailControlRendererFunction } from './util/getMailControlRendererFunction';
import { MailIconColorMap } from './util/mailIconColorMap';
import { getMailRibbonDefinitionsMLR } from './components/MLR/mailMultilineRibbonDefinition';
import { getMailRibbonDefinitionsSLR } from './components/SLR/mailSLRRibbonDefinition';
import { getSelectedTab } from 'owa-mail-ribbon-store/lib/selectors/getSelectedTab';
import { lazyModule as lazyActionControlsSectionsModule } from 'owa-compose-actioncontrols-sections/lib/sections';
import { lazyModule as lazyFormatControlsSectionsModule } from 'owa-compose-formatcontrols-sections/lib/sections';
import { lazyModule as lazyMailComposeControlsSectionsModule } from 'owa-mail-compose-control-sections/lib/sections';
import { lazyModule as lazyRibbonSectionsModule } from 'owa-mail-ribbon-sections/lib/sections';
import { CopilotCommandCenter } from 'owa-copilot-command-center';
import saveSelectedTab from 'owa-mail-ribbon-store/lib/actions/saveSelectedTab';
import { isFeatureEnabled } from 'owa-feature-flags';
import { observer } from 'owa-mobx-react';
import { LazyCopilotDab } from 'owa-copilot-dab';
import { LazyCopilotDabMailSuggestions } from 'owa-copilot-dab-mail';
import { determineCopilotDabMailEntrypoint } from 'owa-copilot-dab-mail/lib/utils/determineCopilotDabMailEntrypoint';
import { shouldOmitPopover } from 'owa-copilot-dab-mail/lib/utils/shouldOmitPopover';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';

const getAttachmentPickers = owaComputedFn(
    (
        attachmentMultiPicker: AttachmentMultiPicker,
        attachmentInlinePickers: AttachmentMultiPicker
    ): AttachmentPickers => {
        return {
            addFilesAsAttachments: attachmentMultiPicker,
            addImagesInline: attachmentInlinePickers,
        };
    }
);

export const getMailCommandRibbonProps: (
    attachmentInlinePickers: AttachmentMultiPicker,
    attachmentMultiPicker: AttachmentMultiPicker
) => CommandRibbonProps = owaComputedFn(
    (
        attachmentInlinePickers: AttachmentMultiPicker,
        attachmentMultiPicker: AttachmentMultiPicker
    ): CommandRibbonProps => {
        const attachmentPickers = getAttachmentPickers(
            attachmentMultiPicker,
            attachmentInlinePickers
        );

        return {
            getSingleLineRibbonDefinition: () => getMailRibbonDefinitionsSLR(attachmentPickers),
            getMultiLineRibbonDefinition: () => getMailRibbonDefinitionsMLR(attachmentPickers),
            iconColorMap: MailIconColorMap,
            ribbonScenarioName: 'Mail',
            controlRendererFunction: getMailControlRendererFunction,
            prioritizedSelectedMailTabId: defaultSelectedTabId,
            onTabClick,
            getExtraTopRibbonControlsBeforeTabs,
            getExtraTopRibbonControlsAfterTabs,
            copilotCommandCenter: <CommandCenterContent />,
            thisWindow: window,
            governedTasks: getMailRibbonGovernedTasks(),
        };
    }
);

const CommandCenterContent: React.FC = observer(() => {
    const mailboxInfo = getModuleContextMailboxInfo();
    if (isFeatureEnabled('copilot-dab-mail-ripcord')) {
        return <CopilotCommandCenter mailboxInfo={mailboxInfo} />;
    } else if (isFeatureEnabled('copilot-dab-ribbon')) {
        return (
            <LazyCopilotDab
                shouldOmitPopover={shouldOmitPopover}
                getEntrypoint={determineCopilotDabMailEntrypoint}
                mailboxInfo={mailboxInfo}
            >
                <LazyCopilotDabMailSuggestions
                    getEntrypoint={determineCopilotDabMailEntrypoint}
                    mailboxInfo={mailboxInfo}
                />
            </LazyCopilotDab>
        );
    } else if (isFeatureEnabled('copilot-dab-bottom-right')) {
        return null;
    } else {
        return <CopilotCommandCenter mailboxInfo={mailboxInfo} />;
    }
}, 'CommandCenterContent');

const getMailRibbonGovernedTasks = owaComputedFn((): GovernedTask[] => {
    return [
        {
            // Import everything in owa-mail-ribbon-sections/lib/sections, such that sections are ready to click.
            task: () => lazyRibbonSectionsModule.importModule('MailCommandRibbonProps'),
            priority: GovernPriority.Ribbon,
            name: 'lazyRibbonSectionsModule',
        },
        {
            // Import everything in owa-compose-formatcontrols-sections/lib/sections, such that sections are ready to click.
            task: () => lazyFormatControlsSectionsModule.importModule('FormatControlsProps'),
            priority: GovernPriority.Compose,
            name: 'lazyFormatControlsSectionsModule',
        },
        {
            // Import everything in owa-compose-actioncontrols-sections/lib/sections, such that sections are ready to click.
            task: () => lazyActionControlsSectionsModule.importModule('ActionControlsProps'),
            priority: GovernPriority.Compose,
            name: 'lazyActionControlsSectionsModule',
        },
        {
            // Import everything in owa-mail-compose-control-sections/lib/sections, such that sections are ready to click.
            task: () =>
                lazyMailComposeControlsSectionsModule.importModule('MailComposeControlsProps'),
            priority: GovernPriority.Compose,
            name: 'lazyMailComposeControlsSectionsModule',
        },
    ];
});

const onTabClick = (tabId: string, targetWindow: Window) => {
    // Saves the selected tab when a new tab is clicked
    saveSelectedTab(tabId, targetWindow);
};

const defaultSelectedTabId = (targetWindow: Window) => getSelectedTab(targetWindow);
